import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtGHS, fmtDateTime } from "@/lib/format";
import { downloadCSV } from "@/lib/csv";
import { HeartPulse, Download } from "lucide-react";
import { toast } from "sonner";

export default function Claims() {
  const { can } = useAuth();
  const { branches } = useBranch();
  const [tab, setTab] = useState("claims");
  const [claims, setClaims] = useState([]);
  const [batches, setBatches] = useState([]);
  const [recon, setRecon] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [fScheme, setFScheme] = useState("all");
  const [fBranch, setFBranch] = useState("all");
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchForm, setBatchForm] = useState({ scheme_id: "", branch_id: "", month: "", note: "" });
  const [settle, setSettle] = useState(null);

  const loadClaims = useCallback(() => {
    api.get("/claims", { params: { scheme_id: fScheme === "all" ? undefined : fScheme, branch_id: fBranch === "all" ? undefined : fBranch } }).then((r) => setClaims(r.data)).catch(() => {});
  }, [fScheme, fBranch]);
  const loadBatches = () => api.get("/claim-batches").then((r) => setBatches(r.data)).catch(() => {});
  const loadRecon = () => api.get("/claims/reconciliation").then((r) => setRecon(r.data)).catch(() => {});
  useEffect(() => { loadClaims(); }, [loadClaims]);
  useEffect(() => { loadBatches(); loadRecon(); api.get("/insurance-schemes").then((r) => setSchemes(r.data)).catch(() => {}); }, []);

  const openBatch = () => { setBatchForm({ scheme_id: "", branch_id: "", month: "", note: "" }); setBatchOpen(true); };
  const createBatch = async () => {
    if (!batchForm.scheme_id || !batchForm.branch_id) return toast.error("Choose a scheme and a branch");
    try {
      const { data } = await api.post("/claim-batches", batchForm);
      toast.success(`Batch ${data.batch_no} created (${data.claim_count} claims)`);
      setBatchOpen(false); loadClaims(); loadBatches(); loadRecon();
    } catch (e) { toast.error(apiError(e)); }
  };
  const doSettle = async () => {
    try {
      await api.post(`/claim-batches/${settle.batch.id}/settle`, { status: settle.status, paid_amount: settle.status === "paid" ? Number(settle.paid_amount) : 0, note: settle.note });
      toast.success(`Batch ${settle.status}`); setSettle(null); loadBatches(); loadClaims(); loadRecon();
    } catch (e) { toast.error(apiError(e)); }
  };
  const exportClaims = () => {
    if (!claims.length) return toast.info("No claims to export");
    downloadCSV("insurance-claims.csv", claims, [
      { key: "invoice_no", label: "Invoice" }, { key: "customer_name", label: "Patient" },
      { key: "scheme_name", label: "Scheme" }, { key: "branch_name", label: "Branch" },
      { key: "amount", label: "Amount" }, { key: "batch_no", label: "Batch" },
      { key: "status", label: "Status" }, { key: "created_at", label: "Created" },
    ]);
  };

  return (
    <div>
      <PageHeader title="Insurance Claims" subtitle="Batch pending claims per scheme & branch, then reconcile paid vs rejected"
        actions={can("finance.claims") && <Button data-testid="new-claim-batch-btn" onClick={openBatch}><HeartPulse className="h-4 w-4 mr-1.5" /> Batch pending claims</Button>} />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="claims" data-testid="tab-claims">Claims</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-claim-batches">Batches</TabsTrigger>
          <TabsTrigger value="recon" data-testid="tab-reconciliation">Reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent value="claims" className="mt-4">
          <Card className="p-3 mb-4 shadow-sm flex flex-col sm:flex-row gap-3">
            <Select value={fScheme} onValueChange={setFScheme}>
              <SelectTrigger data-testid="claims-scheme-filter" className="w-full sm:w-56"><SelectValue placeholder="Scheme" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All schemes</SelectItem>{schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={fBranch} onValueChange={setFBranch}>
              <SelectTrigger data-testid="claims-branch-filter" className="w-full sm:w-56"><SelectValue placeholder="Branch" /></SelectTrigger>
              <SelectContent><SelectItem value="all">All branches</SelectItem>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
            <Button variant="outline" className="sm:ml-auto" data-testid="export-claims-btn" onClick={exportClaims}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          </Card>
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Invoice</th><th className="py-2.5 px-3">Patient</th><th className="py-2.5 px-3">Scheme</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3 text-right">Amount</th><th className="py-2.5 px-3">Batch</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3">When</th></tr>
              </thead>
              <tbody>
                {claims.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No claims</td></tr>}
                {claims.map((c) => (
                  <tr key={c.id} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3 font-mono text-xs">{c.invoice_no}</td>
                    <td className="py-2 px-3">{c.customer_name || "—"}</td>
                    <td className="py-2 px-3 text-muted-foreground">{c.scheme_name || "Unknown scheme"}</td>
                    <td className="py-2 px-3 text-muted-foreground">{c.branch_name}</td>
                    <td className="py-2 px-3 text-right font-mono">{fmtGHS(c.amount)}</td>
                    <td className="py-2 px-3 font-mono text-xs text-muted-foreground">{c.batch_no || "—"}</td>
                    <td className="py-2 px-3"><StatusBadge value={c.status} label={c.status} /></td>
                    <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="batches" className="mt-4">
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Batch No.</th><th className="py-2.5 px-3">Scheme</th><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3 text-right">Claims</th><th className="py-2.5 px-3 text-right">Claimed</th><th className="py-2.5 px-3 text-right">Paid</th><th className="py-2.5 px-3">Status</th><th className="py-2.5 px-3"></th></tr>
              </thead>
              <tbody>
                {batches.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-muted-foreground">No claim batches</td></tr>}
                {batches.map((b) => (
                  <tr key={b.id} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3 font-mono text-xs">{b.batch_no}</td>
                    <td className="py-2 px-3">{b.scheme_name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{b.branch_name}</td>
                    <td className="py-2 px-3 text-right font-mono">{b.claim_count}</td>
                    <td className="py-2 px-3 text-right font-mono">{fmtGHS(b.total_amount)}</td>
                    <td className="py-2 px-3 text-right font-mono">{b.status === "paid" ? fmtGHS(b.paid_amount) : "—"}</td>
                    <td className="py-2 px-3"><StatusBadge value={b.status} label={b.status} /></td>
                    <td className="py-2 px-3 text-right whitespace-nowrap">
                      {can("finance.claims") && b.status === "submitted" && (
                        <>
                          <Button size="sm" data-testid={`pay-batch-${b.batch_no}`} onClick={() => setSettle({ batch: b, status: "paid", paid_amount: b.total_amount, note: "" })}>Mark paid</Button>
                          <Button size="sm" variant="outline" className="ml-1" data-testid={`reject-batch-${b.batch_no}`} onClick={() => setSettle({ batch: b, status: "rejected", paid_amount: 0, note: "" })}>Reject</Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="recon" className="mt-4">
          <Card className="shadow-sm overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase tracking-wider text-muted-foreground text-left">
                <tr><th className="py-2.5 px-3">Branch</th><th className="py-2.5 px-3">Scheme</th><th className="py-2.5 px-3 text-right">Pending</th><th className="py-2.5 px-3 text-right">Submitted</th><th className="py-2.5 px-3 text-right">Paid</th><th className="py-2.5 px-3 text-right">Rejected</th></tr>
              </thead>
              <tbody>
                {recon.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No claim activity</td></tr>}
                {recon.map((r, i) => (
                  <tr key={i} className="border-t border-border hover:bg-muted/50">
                    <td className="py-2 px-3">{r.branch_name}</td>
                    <td className="py-2 px-3 text-muted-foreground">{r.scheme_name || "Unknown scheme"}</td>
                    <td className="py-2 px-3 text-right font-mono text-amber-600">{fmtGHS(r.pending)}</td>
                    <td className="py-2 px-3 text-right font-mono text-sky-600">{fmtGHS(r.submitted)}</td>
                    <td className="py-2 px-3 text-right font-mono text-emerald-600">{fmtGHS(r.paid)}</td>
                    <td className="py-2 px-3 text-right font-mono text-rose-600">{fmtGHS(r.rejected)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={batchOpen} onOpenChange={setBatchOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Batch pending claims</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Groups pending claims for the chosen insurer and branch into one submission. Pick a month to batch only that period's claims.</p>
            <div><Label>Scheme</Label>
              <Select value={batchForm.scheme_id} onValueChange={(v) => setBatchForm((f) => ({ ...f, scheme_id: v }))}>
                <SelectTrigger data-testid="batch-scheme-select"><SelectValue placeholder="Insurer" /></SelectTrigger>
                <SelectContent>{schemes.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Branch</Label>
              <Select value={batchForm.branch_id} onValueChange={(v) => setBatchForm((f) => ({ ...f, branch_id: v }))}>
                <SelectTrigger data-testid="batch-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Month (optional)</Label><Input type="month" data-testid="batch-month-input" value={batchForm.month} onChange={(e) => setBatchForm((f) => ({ ...f, month: e.target.value }))} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setBatchOpen(false)}>Cancel</Button><Button data-testid="submit-claim-batch-btn" onClick={createBatch}>Create batch</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!settle} onOpenChange={(o) => !o && setSettle(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{settle?.status === "paid" ? "Mark batch paid" : "Reject batch"} — {settle?.batch?.batch_no}</DialogTitle></DialogHeader>
          {settle && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">Claimed {fmtGHS(settle.batch.total_amount)} · {settle.batch.claim_count} claims</div>
              {settle.status === "paid" && <div><Label>Amount paid (GHS)</Label><Input type="number" step="0.01" data-testid="settle-amount-input" value={settle.paid_amount} onChange={(e) => setSettle((s) => ({ ...s, paid_amount: e.target.value }))} /></div>}
              <div><Label>Note</Label><Input data-testid="settle-note-input" value={settle.note} onChange={(e) => setSettle((s) => ({ ...s, note: e.target.value }))} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setSettle(null)}>Cancel</Button><Button data-testid="submit-settle-btn" onClick={doSettle}>Confirm</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
